import {
  ApplicationConfig,
  inject,
  PLATFORM_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
  REQUEST,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { InitialService } from './services/initial.service';
import { importProvidersFrom } from '@angular/core';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { GlobalDialogService } from './services/dialog.service';
import { apiList } from './services/apiList.service';
import { provideServiceWorker } from '@angular/service-worker';
import { initializeApp } from 'firebase/app';
import { environment } from '../environments/environment';
import {
  provideClientHydration,
  withEventReplay,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { getInterceptor } from './core/interceptors/get-interceptor.service';
import { setInterceptor } from './core/interceptors/set-interceptor.service';
import { I18nService } from './services/i18n.service';
import { ServerClockService } from './services/server-clock.service';
import { readStoredLanguage } from './core/helpers/language-storage.helper';

initializeApp(environment.firebaseConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),

    provideClientHydration(withEventReplay(), withIncrementalHydration()),

    provideRouter(
      routes,
      withViewTransitions(),
      withComponentInputBinding(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      })
    ),

    // ✅ Single provideHttpClient registration with all options combined
    provideHttpClient(
      withFetch(),
      withInterceptors([getInterceptor, setInterceptor])
    ),

    importProvidersFrom(NgxUiLoaderModule),

    provideServiceWorker('/firebase-sw.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:3000',
    }),

    provideAppInitializer(async () => {
      const platformId = inject(PLATFORM_ID);
      const isBrowser = isPlatformBrowser(platformId);
      const initialService = inject(InitialService);
      const i18n = inject(I18nService);
      const serverClock = inject(ServerClockService);

      const request = inject(REQUEST, { optional: true }) as {
        headers?: { get?: (name: string) => string | null };
      } | null;
      const cookieHeader = request?.headers?.get?.('cookie') ?? null;
      const lang = isBrowser
        ? readStoredLanguage()
        : readStoredLanguage(cookieHeader);

      await i18n.initialize(lang);

      // Browser-only side effects — never block SSR bootstrap
      if (isBrowser) {
        await serverClock.sync();
        setTimeout(() => {
          initialService.getLanguages();
          initialService.setUpGoogleScript().catch(() => {});
        }, 0);
      }
    }),

    MessageService,
    { provide: DialogService, useClass: GlobalDialogService },
    apiList,
    ConfirmationService,
    DynamicDialogRef,
    provideAnimationsAsync(),
  ],
};
